import { $dy__fetchWithToken } from "~/modules/nuxt3-module-doyac/composables/fetch"
import { type IGBookGoods } from "./ig-book-goods"
import { type IGShopGoods } from "./ig-shop-goods"
import type { ApiListStructure } from "~/modules/nuxt3-module-doyac/composables/common"
import { useIGUserStore } from "../stores/user"
import type {IGCourse} from "~/modules/nuxt3-module-ingang/composables/ig-course";
import { useDYAppConfig } from "~/modules/nuxt3-module-doyac/composables/config"
import type { EdgeCacheParams } from "~/modules/nuxt3-module-doyac/composables/edge-cache"

// ============================================ MODEL ============================================
export default interface IGCart<DYProduct = IGCourse> {
  no: number
  userId: string
  typedPk: string
  goodsInfo: IGCourse | IGBookGoods | IGShopGoods | DYProduct
  goods_table: string
  goods_key: number
  courseOrderFlag: number
  courseOrderFlagData: { Name:string, ShortName:string }
  shopGoodNo: number|null
  shopGoodOptionValue: string
  akboMrOrderFlag: number|null
  numberOfGoods: number
  selected?: boolean
}

interface cartStoreParams {
  childProductSelected : (string | string[])[]
  numberOfGoods : number
  typedPk : string
}
// ============================================ MODEL ============================================

// ============================================ REPOSITORY ============================================
export interface IGCartRepositoryListParams extends EdgeCacheParams {
  no?: number
  userId?: string
  /**
   * 해당 유저의 장바구니 정보를 모두가져올 때 true로 보냄
   */
  getCartInfo?: boolean
  typedPk?: string
  goods_table?: string
  goods_key?: number
  courseOrderFlag?: number
  shopGoodNo?: string
  shopGoodOptionValue?: string
  akboMrOrderFlag?: string
  numberOfGoods?: number
}

export interface IGCartPatchParams {
  numberOfGoods?: number
}

// DYProduct같은 별도 선언된 인터페이스를 써버리면 임플단계에서 이걸 썼을 때에도 추상화된 DYProduct값밖에 접근이 안되기 때문에 제너릭을 사용해서 임플에서 직접 주입한 객체에 접근이 가능하다
export function useCartPage<Product = IGCourse> () {
  const { remove } = useCartApi()
  const items = ref<IGCart<Product>[]>([])
  const userStore = useIGUserStore()

  const setItems = (newItems: IGCart[]) => {
    items.value = newItems
  }

  const removeCart = async (value: IGCart<Product>) => {
    setItems(items.value.filter(item => item !== value) as IGCart[])

    // 2026-08-19 [버그수정] `indexOf` 는 못 찾으면 **-1** 을 주는데 `!== undefined` 만 봐서
    //   `splice(-1, 1)` 이 돌아 **엉뚱한(마지막) 항목**이 cartInfo 에서 지워지고 있었다.
    //   목록에 없을 때는 아무것도 하지 않아야 한다.
    const targetIndex = userStore.cartInfo?.indexOf(value.typedPk)
    if (targetIndex !== undefined && targetIndex > -1) {
      userStore.cartInfo?.splice(targetIndex, 1)
    }
    await remove(value.no)
  }

  return {
    items,
    setItems,
    removeCart
  }
}

export function useCartApi () {
  const { API_BASE_URL } = useDYAppConfig()

  const get = async (pk: number | string) => {
    return await $dy__fetchWithToken<IGCart[]>(`${API_BASE_URL}/api/carts/${pk}`)
  }

  const remove = async (pk: number | string) => {
    return await $dy__fetchWithToken<IGCart[]>(`${API_BASE_URL}/api/carts/${pk}`, {method:'delete'})
  }

  const store = async (cartStoreParams:cartStoreParams) => {
    return await $dy__fetchWithToken<{success:boolean, message:string, result:string}>(`${API_BASE_URL}/api/carts`, {method:'post', body:cartStoreParams})
  }

  const getList = async (params?: IGCartRepositoryListParams) => {
    return await $dy__fetchWithToken<ApiListStructure<IGCart>>(`${API_BASE_URL}/api/carts`, { params })
  }

  const patch = async (pk:number, params?: IGCartPatchParams) => {
    await $dy__fetchWithToken<any>(`${API_BASE_URL}/api/carts/`+pk, {method:'patch', body:params})
  }

  return {
    get, store, remove, getList, patch
  }
}
// ============================================ REPOSITORY ============================================
